import './Navigation.css';

import React, { JSX, useContext } from 'react';
import { useLocation, useMatch, useNavigate } from 'react-router';
import { Routes } from '../app/Routes';
import { Building } from '../assets/svg/Building';
import { List } from '../assets/svg/List';
import { Order } from '../assets/svg/Order';
import { Orders } from '../assets/svg/Orders';
import { Requests } from '../assets/svg/Requests';
import { Settings } from '../assets/svg/Settings';
import { localized } from '../config/localization';
import { AuthContext, AuthContextProps } from '../services/AuthContext';

type Props = {
  readonly show: boolean;
};

export function Navigation({ show }: Props): JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();

  const { isAdmin } = useContext<AuthContextProps>(AuthContext);

  const isAdminOrders = useMatch(Routes.adminOrders(':status')) !== null;
  const isAdminUsers = useMatch(Routes.adminUsers(':status')) !== null;
  const isAdminCustomers = useMatch(Routes.ADMIN_CUSTOMERS) !== null;
  const isCustomerOrders = useMatch(Routes.CUSTOMER_ORDERS) !== null;
  const isCheckout = location.pathname.startsWith(Routes.CHECKOUT);
  const isUserSettings = location.pathname.startsWith(Routes.userSettings());

  const isActive = (path: string) => {
    const pathName = location?.pathname;
    if (path === pathName) {
      return true;
    }
    if (pathName && (pathName.match(/\//g) || []).length > 1) {
      const pathNameRoot = '/' + pathName.split('/')[1];
      return path === pathNameRoot;
    }
    return false;
  };

  if (!show) {
    return <div className="navigation-background" />;
  }

  return (
    <div className="navigation-background">
      <NavigationItem
        label={localized.CALCULATOR}
        route={Routes.CHECKOUT_UPLOAD}
        isActive={isCheckout || isActive(Routes.CHECKOUT_UPLOAD)}
        navigate={navigate}
        dataCy="menu-calculator"
      >
        <Order />
      </NavigationItem>
      <NavigationItem
        label={localized.RECENT_ORDERS}
        route={Routes.CUSTOMER_ORDERS}
        isActive={isCustomerOrders}
        navigate={navigate}
        dataCy="menu-recent-orders"
      >
        <List />
      </NavigationItem>
      <NavigationItem
        label={localized.ADMIN_ORDERS}
        route={Routes.adminOrders('submitted_withdraw')}
        isActive={isAdminOrders}
        navigate={navigate}
        rendered={isAdmin}
        dataCy="menu-admin-orders"
      >
        <Orders />
      </NavigationItem>
      <NavigationItem
        rendered={isAdmin}
        label={localized.USER_MANAGEMENT}
        route={Routes.adminUsers('newuser')}
        isActive={isAdminUsers}
        navigate={navigate}
        dataCy="menu-customer-management"
      >
        <Requests />
      </NavigationItem>
      <NavigationItem
        rendered={isAdmin}
        label={localized.CUSTOMER_MANAGEMENT}
        route={Routes.ADMIN_CUSTOMERS}
        isActive={isAdminCustomers}
        navigate={navigate}
        dataCy="menu-user-management"
      >
        <Building />
      </NavigationItem>
      <NavigationItem
        label={localized.SETTINGS}
        route={Routes.userSettings()}
        isActive={isUserSettings}
        navigate={navigate}
        dataCy="menu-settings"
      >
        <Settings />
      </NavigationItem>
    </div>
  );
}

type NavigationItemProps = {
  readonly rendered?: boolean;
  readonly label: string;
  readonly route: string;
  readonly isActive: boolean;
  readonly navigate: (route: string) => void;
  readonly children?: React.ReactNode;
  readonly dataCy?: string;
};

function NavigationItem({
  rendered = true,
  label,
  route,
  isActive,
  navigate,
  children,
  dataCy,
}: NavigationItemProps): JSX.Element | null {
  if (!rendered) {
    return null;
  }

  return (
    <div
      className={`navigation_item ${isActive ? 'navigation_item-active' : ''}`}
      onClick={() => navigate(route)}
      data-cy={dataCy}
    >
      {children}
      {label}
    </div>
  );
}
